import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { createSelector } from "@reduxjs/toolkit";
import { ProxyErrorReport } from "@intelligems/ig-embedded-preview/messages";
import { verifySemver } from "@src/store/slices/experienceBuilder/common/utils/verifySemver";
import { minEmbeddedPreviewVersion } from "@intelligems/ig-embedded-preview/embeddedPreviewVersion";
import { getPluginConfigVersion } from "@src/store/slices/experienceBuilder/common/selectors/getPluginConfigVersion";

const { getLoadState, getPluginVersion, getPath, getPageHtmlUnavailable } =
  experienceBuilderSlice.selectors;

export const getPageState = createSelector(
  getLoadState,
  getPluginVersion,
  getPluginConfigVersion,
  getPath,
  getPageHtmlUnavailable,
  (
    loadState,
    pluginVersion,
    pluginConfigVersion,
    path,
    pageHtmlUnavailable
  ) => {
    const pluginSemver = verifySemver(pluginVersion, minEmbeddedPreviewVersion);
    const pluginConfigSemver = verifySemver(
      pluginConfigVersion,
      minEmbeddedPreviewVersion
    );
    switch (loadState?.type) {
      case "ready": {
        if (pluginSemver === "valid") {
          // Only surface a capture failure once everything underneath is
          // actually healthy — otherwise the root cause (plugin mismatch,
          // proxy error, still loading) is what the user needs to see.
          if (pageHtmlUnavailable) {
            return { type: "pageHtmlUnavailable" } as const;
          }
          return { type: "ready" } as const;
        }
        if (pluginConfigSemver === "unknown") {
          return { type: "loading" } as const;
        }
        if (pluginConfigSemver === "invalid") {
          return { type: "pluginMismatch" } as const;
        }
        if (pluginSemver === "invalid") {
          return { type: "pluginCacheInvalid" } as const;
        }
        return { type: "ready" } as const;
      }
      case "error": {
        if (pluginConfigSemver === "invalid") {
          return { type: "pluginMismatch" } as const;
        }
        if (loadState.subtype === "plugin") {
          return { type: "pluginTimeout" } as const;
        }
        if (
          loadState.subtype === "blocked" &&
          typeof loadState.url === "string"
        ) {
          return {
            type: "networkBlocked",
            url: loadState.url,
          } as const;
        }
        return {
          type: "proxyTimeout",
          proxyError: loadState.proxyError as ProxyErrorReport | undefined,
        } as const;
      }
      default:
        if (pluginConfigSemver === "invalid") {
          return { type: "pluginMismatch" } as const;
        }
        return { type: "loading" } as const;
    }
  }
);
